import type { ReactNode } from "react";
import {
  IconUpload,
  IconLayout,
  IconMonitor,
  IconPencil,
  IconPlay,
  IconPlus,
  IconClockRecord,
  IconSliders,
  IconChevron,
  IconTablet,
  IconVolume,
} from "../control/ui/icons";
import { CrownMark } from "../control/ui/Crown";

/**
 * WHAT EACH SURFACE TEACHES, in one place.
 *
 * Two pieces of user feedback, the same day: "a banner every single time someone visits
 * until they click don't show again — just to make sure they understand they have
 * options", and "a how-to page outlining how each button affects clips and where you can
 * go to make your own custom layout". The confusion being reported is not pairing — it
 * is regions, feeds and custom views: the product's whole point, invisible to someone
 * who doesn't know the tiles are buttons.
 *
 * So each surface carries its own guide (SurfaceGuide renders these as a dialog, the
 * wall-intro pattern generalized), and /how renders the same arrays as a page. ONE
 * source of truth, two renders — the landing's QR lesson is why: marketing prose that
 * named a deleted mechanism survived it by a full release, because the copy lived
 * nowhere near the code. These steps name mechanisms too, so they live in one file the
 * check suite can hold against the surfaces they describe.
 *
 * THE ICONS ARE THE REAL ONES — the same components the buttons render, not lookalike
 * emoji — so a control that changes its glyph changes its guide row with it, and a
 * reader matches the row to the button by shape rather than by faith.
 *
 * THE WALL IS HERE TOO, as the combined guide it always needed: the wall is a screen
 * before it is a marketplace, and it is the first screen most visitors ever see (`/`
 * plays it), so its guide teaches the screen mechanics that work there PLUS the one
 * fact no other screen has — squares are owned. The binding money rules stay where
 * they always bound: the buy dialog's fine print, which states every rule the server
 * enforces at the moment it matters. WallIntroDialog is deleted; this replaced it.
 */

export type GuideSurface = "screen" | "wall" | "infinity" | "views" | "editor" | "feed";

export type GuideStep = {
  icon: ReactNode;
  title: string;
  body: string;
};

export type GuideContent = {
  /** The big first line — somebody who reads nothing else should leave with this. */
  headline: string;
  /** One sentence under it, the surface's whole model. */
  sub: string;
  steps: ReadonlyArray<GuideStep>;
};

/*
 * The steps a wall square and an ordinary tile share, written once. The wall IS a
 * screen — the crown, the hover controls, the session and the remote all work there —
 * so its guide and the screen's compose from the same rows and cannot drift apart.
 */
const STEP_CROWN: GuideStep = {
  icon: <CrownMark size={18} />,
  title: "Crown the clips you love",
  body: "The crown in a tile's corner faps the clip — it lands in your Faps collection to replay any time.",
};
const STEP_HOVER: GuideStep = {
  icon: <IconVolume width={18} height={18} />,
  title: "Hover a tile for its own controls",
  body: "Sound, photo time, sync and loop sit in the tile's top corner — run one tile loud and the rest silent.",
};
const STEP_GOON: GuideStep = {
  icon: <IconClockRecord width={18} height={18} />,
  title: "Goon starts a session",
  body: "The timer, your edges, the history. The screen keeps playing either way — only the counting starts.",
};
const STEP_REMOTE: GuideStep = {
  icon: <IconTablet width={18} height={18} />,
  title: "The code pairs a remote",
  body: "Show the code chip in the bar, open /remote on your phone, type it — now the couch drives this screen.",
};

/**
 * The wall's card, built ONCE for both truths about the free pool. `anyFree` swaps a
 * single sentence: whether a square that has drifted to its floor is currently free to
 * claim. The rule comes from the free-claims feature (the old wall intro's `anyFree`
 * prop): the copy mentions the free pool only while it actually EXISTS on the board,
 * so the rules never promise a dark switch — which is why the static renders (/how,
 * the check harness) get `GUIDES.wall` = wallContent(false), and only DisplayApp,
 * which can see the board, ever hands out the free variant.
 *
 * `premium` is the Premium Wall's card: the same rules with one difference said up
 * front — everyone watches, members claim. Same surface ("wall"), same auto-open gate,
 * same dismissal flag: a person who has read one wall's card has read the other's.
 */
function wallContent(anyFree: boolean, premium = false): GuideContent {
  const claim = anyFree
    ? "The price is on the square — and a square that's drifted down to its floor is free to claim, one a day. Tap one to grab it, and your clips play there under your name on every screen showing the wall, until somebody buys it off you. No refunds: the time up there is what you bought."
    : "The price is on the square — tap one to grab it, and your clips play there under your name on every screen showing the wall, until somebody buys it off you. No refunds: the time up there is what you bought.";
  return {
    headline: premium ? "This is the Premium Wall" : "This is the Community Wall",
    sub: premium
      ? "A screen like any other — except everyone sees it, and only GoonLords Premium members hold its squares."
      : "A screen like any other — except everyone sees it, and every square is owned. Modify at your peril.",
    steps: [
      {
        icon: <IconLayout width={18} height={18} />,
        title: premium ? "Every square is for members" : "Every square is for sale",
        body: premium
          ? `Squares here are for GoonLords Premium members — everyone can watch, members claim. ${claim}`
          : claim,
      },
      STEP_CROWN,
      STEP_HOVER,
      STEP_GOON,
      {
        icon: <IconPlus width={18} height={18} />,
        title: "Want a board that's all yours?",
        body: "The wall itself can't be edited. Build in Views makes your own layout — your tiles, your feeds, nobody outbidding you.",
      },
      STEP_REMOTE,
    ],
  };
}

/** The free-pool variant, for the one host that can see the board (DisplayApp). */
export const WALL_GUIDE_FREE: GuideContent = wallContent(true);
/** The Premium Wall's card, in both truths about its free pool. */
export const PREMIUM_WALL_GUIDE: GuideContent = wallContent(false, true);
export const PREMIUM_WALL_GUIDE_FREE: GuideContent = wallContent(true, true);

/**
 * The wall card for what a screen can see: which wall, and whether a free square exists on
 * it right now. `undefined` for the Community Wall with no free pool — that is `GUIDES.wall`,
 * the default the SurfaceGuide already renders, so the host overrides content only when
 * there is something to override.
 */
export function wallGuide(premium: boolean, anyFree: boolean): GuideContent | undefined {
  if (premium) return anyFree ? PREMIUM_WALL_GUIDE_FREE : PREMIUM_WALL_GUIDE;
  return anyFree ? WALL_GUIDE_FREE : undefined;
}

/*
 * FAPS, the same way: the currency is a switch, and the copy speaks it only where the
 * board says it is on. `withFaps` rewrites the two rows the currency touches — the
 * crown now EARNS, and squares are priced in FAPS rather than dollars — and leaves
 * every other row alone, so the FAPS guides cannot drift from the ones they are built
 * from. The statics (/how, the check harness) keep the non-FAPS copy while the switch
 * is dark; DisplayApp hands these out when the board reports `currency: "faps"`.
 */
const STEP_CROWN_FAPS: GuideStep = {
  ...STEP_CROWN,
  body: "The crown in a tile's corner faps the clip — it lands in your Faps collection, and every fap earns you a Fap for the wall, up to a daily cap.",
};
const WALL_SALE_BODY_FAPS =
  "The price is on the square, in Faps — earn them by fapping, gooning and opening your daily chest, then tap a square to grab it. Your clips play there under your name on every screen showing the wall, until somebody buys it off you. No refunds: the time up there is what you bought.";

export function withFaps(content: GuideContent): GuideContent {
  return {
    ...content,
    steps: content.steps.map((s) =>
      s === STEP_CROWN
        ? STEP_CROWN_FAPS
        : s.title === "Every square is for sale"
          ? { ...s, body: WALL_SALE_BODY_FAPS }
          : s.title === "Every square is for members"
            ? { ...s, body: `Squares here are for GoonLords Premium members — everyone can watch, members claim. ${WALL_SALE_BODY_FAPS}` }
            : s,
    ),
  };
}

export const WALL_GUIDE_FAPS: GuideContent = withFaps(wallContent(false));

export const GUIDES: Record<GuideSurface, GuideContent> = {
  screen: {
    headline: "This screen is yours to steer",
    sub: "Every tile is a button, and nothing you press interrupts the goon.",
    steps: [
      {
        icon: <IconLayout width={18} height={18} />,
        title: "Tap any tile to change its feed",
        body: "Each tile plays its own stream. Tap one and pick what goes there — a collection, the tags you crave, or trending.",
      },
      STEP_CROWN,
      STEP_HOVER,
      STEP_GOON,
      {
        icon: <IconPencil width={18} height={18} />,
        title: "The pencil makes it yours",
        body: "Edit copies this layout into your own view — resize the tiles, swap every feed, keep it forever.",
      },
      STEP_REMOTE,
    ],
  },
  /*
   * The combined screen + marketplace card. Where the screen guide says "tap a tile to
   * change its feed", the wall must say the opposite — a square is OWNED, and tapping
   * it opens the sale — and where the screen offers the pencil, the wall cannot (no
   * edited wall exists; see DisplayApp's onEdit), so that step points at Build instead,
   * which is the road out of exactly the confusion users reported. Money copy states
   * only what the buy dialog's fine print binds; the details live there, at the moment
   * they matter.
   */
  wall: wallContent(false),
  /*
   * Infinity Walls: a scroll, not a board. The screen guide's "tap a tile to change its
   * feed" and "the pencil makes it yours" are both untrue here — nothing is authored,
   * nothing is edited — so the card says the three things that ARE true: swipe, double
   * tap, crown. Short on purpose: the surface explains itself the moment it moves.
   */
  infinity: {
    headline: "This is Infinite Scroll",
    sub: "A wall dealt at random, every tile on a loop — and another one every swipe.",
    steps: [
      {
        icon: <IconChevron width={18} height={18} />,
        title: "Swipe for a new wall",
        body: "Scroll down and the next wall is already playing — a fresh layout, fresh clips, no end to it. Random deals from the whole library; Enhanced builds every wall around one keyword; Colorwave dresses every wall in one colour; Solo Girls is one woman per tile; Build a Slut lets you pick her face, tits, body, hair and style, then scrolls her; Goonette of the Hill keeps the one you pick and sends three challengers; GoonTok is one video at a time, the crowd\u2019s favourites mixed with surprises.",
      },
      {
        icon: <IconVolume width={18} height={18} />,
        title: "Double-tap to fap",
        body: "Double-tap a tile to fap it. Sound lives on the speaker button up top; tapping the video never changes it. Except Goonette of the Hill — there a tap picks your champion.",
      },
      {
        icon: <IconClockRecord width={18} height={18} />,
        title: "Or let it scroll itself",
        body: "☰ → Auto-scroll picks how long each wall stays — 5 seconds to a minute. Swipe any time; the clock restarts on the new wall.",
      },
      STEP_CROWN,
    ],
  },
  feed: {
    headline: "The Feed is where gooners talk",
    sub: "Post a clip, crown the ones you love, and climb today's boards.",
    steps: [
      {
        icon: <IconUpload width={18} height={18} />,
        title: "Post a status — with a clip on it",
        body: "Say what you're gooning to, then put a clip on it from your faps, the catalog or your own uploads — or share one of your public collections. Every status carries one. Your uploads show after a moderator clears them.",
      },
      {
        icon: <CrownMark size={18} />,
        title: "Crown it, say something",
        body: "The crown on a post is the crown on its clip — it lands in your Faps and pays like any crown. Comments run one reply deep.",
      },
      {
        icon: <IconClockRecord width={18} height={18} />,
        title: "Climb today's boards",
        body: "Goon time, Faps earned and crowns given, top five each, reset at midnight UTC. Public profiles only — flip yours on to be ranked.",
      },
    ],
  },
  views: {
    headline: "Views are channels you build",
    sub: "A view is a layout of tiles, each with its own feed — screens play views.",
    steps: [
      {
        icon: <IconPlay width={18} height={18} />,
        title: "Quick setups play instantly",
        body: "Prebuilt layouts, one tap to watch. If one is close but not right, open it and remix.",
      },
      {
        icon: <IconPlus width={18} height={18} />,
        title: "Build starts from blank",
        body: "Lay the tiles yourself and choose what feeds every one. Your builds collect on this page.",
      },
      {
        icon: <IconMonitor width={18} height={18} />,
        title: "Your screens gather here",
        body: "Pair a screen once and it appears beside the library — send any view to any screen from here.",
      },
    ],
  },
  editor: {
    headline: "Build the exact trance you want",
    sub: "The canvas is live — everything you change is changed everywhere, immediately.",
    steps: [
      {
        icon: <IconLayout width={18} height={18} />,
        title: "Drag and resize the tiles",
        body: "Grab a tile to move it, pull its edges to resize. The layout is whatever you leave behind.",
      },
      {
        icon: <IconSliders width={18} height={18} />,
        title: "Click a tile to choose its feed",
        body: "Every tile picks its own source — one of your collections, a set of tags, or trending.",
      },
      {
        icon: <IconMonitor width={18} height={18} />,
        title: "Screens follow your edits live",
        body: "Any screen playing this view tracks the drag as it happens. Build on the laptop, watch it land on the TV.",
      },
      {
        icon: <IconPencil width={18} height={18} />,
        title: "Unsaved views expire",
        body: "A build without a name is temporary. Save gives it a name and keeps it; Play puts it on a screen right now.",
      },
    ],
  },
};

/**
 * Goonette of the Hill's card — same Infinite Scroll shape, but a tap is the pick, not
 * unmute. Sound is the mute control in the header. DisplayApp hands this out when the
 * live view is `infinity:battle`; every other Infinity mode keeps GUIDES.infinity (and
 * /how always does).
 */
export const INFINITY_BATTLE_GUIDE: GuideContent = {
  ...GUIDES.infinity,
  steps: [
    GUIDES.infinity.steps[0]!,
    {
      icon: <IconPlay width={18} height={18} />,
      title: "Tap the one you keep",
      body: "Watch a few seconds, then tap her to keep her. Three challengers show up next. Sound is the mute control up top — a tap here is the pick, not unmute.",
    },
    GUIDES.infinity.steps[2]!,
    GUIDES.infinity.steps[3]!,
  ],
};

/** The screen's guide with the earning crown — DisplayApp's, when the board sells in FAPS. */
export const SCREEN_GUIDE_FAPS: GuideContent = withFaps(GUIDES.screen);

/**
 * THE SCREEN CARD NAMES WHAT IS PLAYING. "This screen is yours to steer" becomes
 * "Latinas ×3 is yours to steer" — the display name of the live view (a quick setup's
 * `name` in shared's builtin definitions, or the view's own). Only the subject of the
 * headline changes; with no name yet (still loading) it is the generic line, never
 * "undefined".
 */
export function namedScreenGuide(base: GuideContent, viewName: string | null | undefined): GuideContent {
  const name = viewName?.trim();
  if (!name) return base;
  return { ...base, headline: base.headline.replace(/^This screen\b/, name) };
}

/** The Premium Wall under FAPS: the Premium card (headline, sub and members step), priced in FAPS. */
export const PREMIUM_WALL_GUIDE_FAPS: GuideContent = withFaps(PREMIUM_WALL_GUIDE);

/**
 * Two per-surface flags, two lifetimes:
 *
 *  - "Don't show this again" — FOREVER, per browser: localStorage, the wall-intro
 *    storage discipline exactly. BLOCKED STORAGE READS AS DISMISSED — a guide that
 *    reappears every visit because its flag cannot persist is worse than no guide, and
 *    the ⓘ is always there.
 *  - "Got it" — FOR THE SESSION, per tab: sessionStorage. Product's call: "Got it lasts
 *    the session" — a reader who has just closed the card is not re-taught it on every
 *    navigation or reload in that tab, and a fresh session teaches it again until they
 *    say stop. A sessionStorage that cannot be read or written adds nothing: the
 *    forever flag already decided, and the host's own per-visit state still holds.
 *
 * Keyed PER SURFACE, so closing Home's card says nothing about the Views page's. The ⓘ
 * ignores both flags — it is a manual open, never an auto-open.
 */
const guideKey = (s: GuideSurface) => `gp:guide:${s}`;
const guideSessionKey = (s: GuideSurface) => `gp:guide-session:${s}`;

/** Should this surface's guide stay shut on arrival? Forever flag OR this session's "Got it". */
export function guideDismissed(surface: GuideSurface): boolean {
  try {
    if (localStorage.getItem(guideKey(surface)) === "1") return true;
  } catch {
    return true;
  }
  return guideDismissedThisSession(surface);
}

export function guideDismissedThisSession(surface: GuideSurface): boolean {
  try {
    return sessionStorage.getItem(guideSessionKey(surface)) === "1";
  } catch {
    return false;
  }
}

/** Written ONLY by the guide's own "Don't show this again" button. */
export function dismissGuideForever(surface: GuideSurface): void {
  try {
    localStorage.setItem(guideKey(surface), "1");
  } catch {
    /* it will show again next visit; harmless */
  }
}

/** Written by "Got it" (and its equivalents — Escape, the scrim): shut for this tab session. */
export function dismissGuideForSession(surface: GuideSurface): void {
  try {
    sessionStorage.setItem(guideSessionKey(surface), "1");
  } catch {
    /* it will show again next visit, as before; harmless */
  }
}
